import { Stack, Skeleton, Heading, Text, Table, Tbody, Link, Icon, useColorModeValue } from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { FiExternalLink } from 'react-icons/fi';
import { useParams } from 'react-router';

import AppCard from 'components/UI/AppCard';
import NoResultCard from 'components/UI/NoResultCard';
import useLciaMethods from 'hooks/data/datasets/lcia/useLciaMethods';
import useV4LciaMethods from 'hooks/data/datasets/V4/lcia/useV4LciaMethods';
import { useIsShowingV4 } from 'hooks/useV4';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import GuestImpactAssessmentDetails from './GuestImpactAssessmentDetails';
import ImpactAssessmentDetails from './ImpactAssessmentDetails';
import { transformToV3MethodArray } from './utils';
import V4ImpactAssessmentDetails from './V4ImpactAssessmentDetails';

type Props = { hideScores?: boolean };

export const ImpactAssessment = ({ hideScores }: Props) => {
  const isShowingV4 = useIsShowingV4();
  const { spold_id } = useParams();
  const { data, isLoading } = useLciaMethods(isShowingV4 || !!hideScores);
  const { data: v4Data, isLoading: isV4Loading } = useV4LciaMethods(spold_id ?? '', !hideScores);
  const iconColor = useColorModeValue('green.500', 'green.200');

  const transformedV4Data = transformToV3MethodArray(v4Data || []);

  return (
    <AppCard>
      <Stack>
        <Skeleton alignSelf={'flex-start'} isLoaded={!isLoading}>
          <Heading as="h4" size="md">
            Impact assessment
          </Heading>
        </Skeleton>
        <Skeleton alignSelf={'flex-start'} isLoaded={!isLoading}>
          <Text maxWidth={{ base: '100%', md: '75%' }}>
            The impact scores of the selected activity are calculated using the selected LCIA method. Expand a row to view the
            contributors to each score.{' '}
            <Link
              href="https://support.ecoinvent.org/lcia-results"
              isExternal
              display="inline-flex"
              alignItems="center"
              gap={1}
              onClick={() => {
                mixpanel.track(MIXPANEL_EVENTS.LCIA_METHOD_INFO_LINK_CLICKED);
              }}
            >
              Learn more about impact assessment <Icon as={FiExternalLink} color={iconColor} />
            </Link>
          </Text>
        </Skeleton>
      </Stack>
      {hideScores ? (
        <GuestImpactAssessmentDetails />
      ) : !data && !v4Data && !isLoading && !isV4Loading ? (
        <Table>
          <Tbody>
            <NoResultCard p={0}>No Impact scores available.</NoResultCard>
          </Tbody>
        </Table>
      ) : isShowingV4 ? (
        <V4ImpactAssessmentDetails isLoading={isV4Loading} methodList={transformedV4Data} uprId={spold_id ?? '-1'} />
      ) : (
        <ImpactAssessmentDetails isLoading={isLoading} methodList={data} spoldId={parseInt(spold_id ?? '-1')} />
      )}
    </AppCard>
  );
};

export default ImpactAssessment;
